import React, { useState } from 'react';
import {
  Alert,
  AlertTitle,
  Box,
  Button,
  Chip,
  CircularProgress,
  Collapse,
  Dialog,
  DialogActions,
  DialogContent,
  DialogProps,
  DialogTitle,
  Divider,
  Stack,
  IconButton,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Paper,
  Typography,
} from '@mui/material';
import {
  CheckCircle,
  Close,
  Description,
  Download,
  ExpandLess,
  ExpandMore,
  FolderOpen,
  Info,
  Upload,
} from '@mui/icons-material';
import { AxiosError } from 'axios';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import FileButton from 'src/components/FileButton';
import { useLcaOrganization } from 'src/common/hooks';
import { LcaNewProductExcelImportSuccess } from '../types';
import { _useLcaProduct, useLcaOrganizationSettings } from '../hooks';

type UploadState = 'idle' | 'uploading' | 'success' | 'error';

type SheetError = {
  sheet: string;
  message: string;
  row?: number;
  field?: string;
};

type FieldError = {
  field: string;
  message: string;
};

interface ImportErrorGroup {
  key: string;
  title: string;
  message: string;
  details?: string[];
  severity: 'error' | 'warning' | 'info';
}

interface StructuredErrorDetail {
  message?: string;
  sheet_errors?: SheetError[];
  field_errors?: FieldError[];
}

const stripStepPrefix = (label: string) => label.replace(/^\d+\.\s*/, '');

export default function NewProductExcelImportDialog(props: DialogProps) {
  const { t } = useTranslation('lca');
  const navigate = useNavigate();
  const organization = useLcaOrganization();
  const [file, setFile] = useState<File | undefined>();
  const [uploadState, setUploadState] = useState<UploadState>('idle');
  const [errors, setErrors] = useState<ImportErrorGroup[]>([]);
  const [successData, setSuccessData] =
    useState<LcaNewProductExcelImportSuccess | null>(null);
  const [expandedErrors, setExpandedErrors] = useState<string[]>([]);

  const { createNewFromExcel, downloadNewProductTemplate } = _useLcaProduct();
  // an imported product starts on the organization's default, like one made
  // in the form
  const { settings: lcaSettings } = useLcaOrganizationSettings(
    props.open ? organization.id : undefined
  );
  const defaultEcoinventVersion = lcaSettings?.default_ecoinvent_version || '';

  const resetState = (clearFile = true) => {
    if (clearFile) {
      setFile(undefined);
    }
    setUploadState('idle');
    setErrors([]);
    setSuccessData(null);
    setExpandedErrors([]);
  };

  const handleClose = () => {
    setTimeout(() => {
      resetState();
    }, 200);
    props?.onClose?.({} as never, 'backdropClick');
  };

  const parseErrorMessage = (errorResponse: AxiosError): ImportErrorGroup[] => {
    const detail = (
      errorResponse.response?.data as { detail?: StructuredErrorDetail }
    )?.detail;

    if (!detail) {
      return [
        {
          key: 'system',
          title: t('product.newImportDialog.errors.importFailedTitle'),
          message: t('product.newImportDialog.errors.importFailedMessage'),
          severity: 'error',
        },
      ];
    }

    const nextErrors: ImportErrorGroup[] = [];
    const groupedSheetErrors = new Map<string, string[]>();

    (detail.sheet_errors || []).forEach((sheetError) => {
      const key =
        sheetError.sheet || t('product.newImportDialog.errors.generalSheet');
      const rowPrefix = sheetError.row
        ? `${t('product.newImportDialog.errors.rowLabel')} ${sheetError.row}: `
        : '';
      const fieldPrefix = sheetError.field ? `${sheetError.field}: ` : '';
      const entry = `${rowPrefix}${fieldPrefix}${sheetError.message}`;
      groupedSheetErrors.set(key, [
        ...(groupedSheetErrors.get(key) || []),
        entry,
      ]);
    });

    groupedSheetErrors.forEach((details, sheetName) => {
      nextErrors.push({
        key: `sheet-${sheetName}`,
        title: `${sheetName} ${t(
          'product.newImportDialog.errors.sheetSuffix'
        )}`,
        message:
          detail.message ||
          t('product.newImportDialog.errors.checkSheetValues'),
        details,
        severity: 'error',
      });
    });

    if (detail.field_errors?.length) {
      nextErrors.push({
        key: 'field-errors',
        title: t('product.newImportDialog.errors.fieldValidationTitle'),
        message: t('product.newImportDialog.errors.fieldValidationMessage'),
        details: detail.field_errors.map(
          (fieldError) => `${fieldError.field}: ${fieldError.message}`
        ),
        severity: 'error',
      });
    }

    if (!nextErrors.length) {
      nextErrors.push({
        key: 'fallback',
        title: t('product.newImportDialog.errors.importFailedTitle'),
        message:
          detail.message ||
          t('product.newImportDialog.errors.importFailedMessage'),
        severity: 'error',
      });
    }

    return nextErrors;
  };

  const toggleErrorExpansion = (key: string) => {
    setExpandedErrors((prev) =>
      prev.includes(key) ? prev.filter((item) => item !== key) : [...prev, key]
    );
  };

  const handleUploadClick = async () => {
    if (!file || !defaultEcoinventVersion) return;

    setUploadState('uploading');
    setErrors([]);

    try {
      const response = await createNewFromExcel.mutateAsync({
        file,
        organizationId: organization.id,
        ecoinventVersion: defaultEcoinventVersion,
      });
      setSuccessData(response.data);
      setUploadState('success');
    } catch (error) {
      setErrors(parseErrorMessage(error as AxiosError));
      setUploadState('error');
    }
  };

  const handleOpenProduct = () => {
    if (!successData?.product_id) return;
    handleClose();
    navigate(`/lca/products/${successData.product_id}`);
  };

  const handleDownloadTemplate = async () => {
    try {
      await downloadNewProductTemplate();
    } catch (error) {
      setErrors(parseErrorMessage(error as AxiosError));
      setUploadState('error');
    }
  };

  const renderIdleContent = () => (
    <Stack spacing={2}>
      <Paper
        elevation={0}
        sx={{
          p: 2.5,
          borderRadius: '24px',
          border: '1px solid',
          borderColor: 'rgba(109, 50, 7, 0.14)',
          background:
            'linear-gradient(135deg, rgba(255,247,232,0.96) 0%, rgba(255,255,255,1) 100%)',
        }}
      >
        <Typography
          variant="overline"
          sx={{
            color: 'primary.main',
            fontWeight: 700,
            letterSpacing: '0.08em',
          }}
        >
          Excel Import
        </Typography>
        <Typography
          variant="h6"
          sx={{ mt: 0.5, mb: 1, color: 'text.primary', fontWeight: 700 }}
        >
          {t('product.newImportDialog.dialogTitles.idle')}
        </Typography>
        <Typography
          variant="body2"
          color="text.secondary"
          sx={{ maxWidth: 560 }}
        >
          {t('product.newImportDialog.description')}
        </Typography>
      </Paper>

      <Paper
        elevation={0}
        sx={{
          p: 2.25,
          borderRadius: '24px',
          border: '1px solid',
          borderColor: 'rgba(109, 50, 7, 0.14)',
          backgroundColor: '#fff',
        }}
      >
        <Stack spacing={1.75}>
          <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.25 }}>
            <Box
              sx={{
                width: 40,
                height: 40,
                borderRadius: '999px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                backgroundColor: '#7E4308',
                color: '#fff',
                fontSize: '14px',
                fontWeight: 700,
                flexShrink: 0,
              }}
            >
              1
            </Box>
            <Box sx={{ pt: 0.25 }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                {stripStepPrefix(
                  t('product.newImportDialog.steps.downloadTitle')
                )}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {t('product.newImportDialog.steps.downloadDescription')}
              </Typography>
            </Box>
          </Box>

          <Button
            variant="outlined"
            startIcon={<Download />}
            onClick={handleDownloadTemplate}
            sx={{ alignSelf: 'flex-start', mt: 0.25 }}
          >
            {t('product.downloadEmptyTemplate')}
          </Button>
        </Stack>
      </Paper>

      <Paper
        elevation={0}
        sx={{
          p: 2.25,
          borderRadius: '24px',
          border: '1px solid',
          borderColor: 'rgba(109, 50, 7, 0.14)',
          backgroundColor: '#fff',
        }}
      >
        <Stack spacing={1.25}>
          <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.25 }}>
            <Box
              sx={{
                width: 40,
                height: 40,
                borderRadius: '999px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                backgroundColor: '#FFF7E8',
                color: '#7E4308',
                fontSize: '14px',
                fontWeight: 700,
                flexShrink: 0,
              }}
            >
              2
            </Box>
            <Box sx={{ pt: 0.25 }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                {stripStepPrefix(t('product.newImportDialog.steps.fillTitle'))}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {t('product.newImportDialog.steps.fillDescription')}
              </Typography>
            </Box>
          </Box>
        </Stack>
      </Paper>

      <Paper
        elevation={0}
        sx={{
          p: 2.25,
          borderRadius: '24px',
          border: '1px solid',
          borderColor: file
            ? 'rgba(126, 67, 8, 0.28)'
            : 'rgba(109, 50, 7, 0.14)',
          backgroundColor: file ? '#FFFDF9' : '#fff',
        }}
      >
        <Stack spacing={1.75}>
          <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.25 }}>
            <Box
              sx={{
                width: 40,
                height: 40,
                borderRadius: '999px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                backgroundColor: file ? '#7E4308' : '#FFF7E8',
                color: file ? '#fff' : '#7E4308',
                fontSize: '14px',
                fontWeight: 700,
                flexShrink: 0,
              }}
            >
              3
            </Box>
            <Box sx={{ pt: 0.25 }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                {stripStepPrefix(
                  t('product.newImportDialog.steps.importTitle')
                )}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {t('product.newImportDialog.steps.importDescription')}
              </Typography>
            </Box>
          </Box>

          <FileButton
            file={file}
            setFile={setFile}
            inputProps={{
              accept:
                '.xlsx,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
            }}
            buttonProps={{
              variant: 'outlined',
              fullWidth: true,
              startIcon: <Upload />,
              sx: { py: 2 },
            }}
          />

          {file && (
            <Alert severity="success" sx={{ borderRadius: '16px' }}>
              <AlertTitle>{t('product.newImportDialog.readyTitle')}</AlertTitle>
              {t('product.newImportDialog.readyDescription', {
                filename: file.name,
              })}
            </Alert>
          )}
        </Stack>
      </Paper>
    </Stack>
  );

  const renderErrorList = () => (
    <Box sx={{ mt: 2 }}>
      {errors.map((error) => (
        <Alert
          key={error.key}
          severity={error.severity}
          sx={{ mb: 1 }}
          action={
            error.details ? (
              <IconButton
                onClick={() => toggleErrorExpansion(error.key)}
                size="small"
              >
                {expandedErrors.includes(error.key) ? (
                  <ExpandLess />
                ) : (
                  <ExpandMore />
                )}
              </IconButton>
            ) : undefined
          }
        >
          <AlertTitle>{error.title}</AlertTitle>
          {error.message}
          <Collapse in={expandedErrors.includes(error.key)}>
            <Box sx={{ mt: 1 }}>
              {error.details && (
                <List dense>
                  {error.details.map((detail) => (
                    <ListItem key={detail} sx={{ py: 0.5 }}>
                      <ListItemIcon sx={{ minWidth: 32 }}>
                        <Info fontSize="small" />
                      </ListItemIcon>
                      <ListItemText primary={detail} />
                    </ListItem>
                  ))}
                </List>
              )}
            </Box>
          </Collapse>
        </Alert>
      ))}
    </Box>
  );

  const renderSuccessContent = () => (
    <Stack spacing={2} sx={{ py: 1 }}>
      <Alert severity="success" icon={<CheckCircle fontSize="inherit" />}>
        <AlertTitle>{t('product.newImportDialog.successTitle')}</AlertTitle>
        {successData?.product_name}
      </Alert>

      {successData && (
        <>
          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
            <Chip
              icon={<Description />}
              label={`${t('product.newImportDialog.fields.templateVersion')} ${
                successData.template_version
              }`}
              variant="outlined"
            />
            <Chip label={successData.filename} variant="outlined" />
          </Box>

          <Paper sx={{ p: 2 }}>
            <Typography variant="subtitle2" gutterBottom>
              {t('product.newImportDialog.successDetailsTitle')}
            </Typography>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  gap: 2,
                }}
              >
                <Typography variant="body2" color="text.secondary">
                  {t('product.newImportDialog.fields.productName')}
                </Typography>
                <Typography variant="body2" fontWeight="medium">
                  {successData.product_name}
                </Typography>
              </Box>
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  gap: 2,
                }}
              >
                <Typography variant="body2" color="text.secondary">
                  {t('product.newImportDialog.fields.file')}
                </Typography>
                <Typography variant="body2">{successData.filename}</Typography>
              </Box>
            </Box>
          </Paper>
        </>
      )}

      {successData?.is_draft && (
        <Alert severity="warning">
          <AlertTitle>{t('product.newImportDialog.draftTitle')}</AlertTitle>
          {t('product.newImportDialog.draftDescription')}
          {!!successData.draft_warnings?.length && (
            <List dense>
              {successData.draft_warnings.map((warning) => (
                <ListItem
                  key={`${warning.field}-${warning.message}`}
                  sx={{ px: 0 }}
                >
                  <ListItemIcon sx={{ minWidth: 28 }}>
                    <Info fontSize="small" />
                  </ListItemIcon>
                  <ListItemText
                    primary={`${warning.field}: ${warning.message}`}
                  />
                </ListItem>
              ))}
            </List>
          )}
        </Alert>
      )}

      <Divider />

      <Alert severity="info">
        <AlertTitle>{t('product.newImportDialog.followUpTitle')}</AlertTitle>
        <List dense>
          {(
            successData?.follow_up_actions || [
              t('product.newImportDialog.defaultFollowUp.image'),
              t('product.newImportDialog.defaultFollowUp.certificates'),
              t('product.newImportDialog.defaultFollowUp.relatedDocuments'),
              t(
                'product.newImportDialog.defaultFollowUp.technicalDocumentation'
              ),
            ]
          ).map((item) => (
            <ListItem key={item} sx={{ px: 0 }}>
              <ListItemIcon sx={{ minWidth: 28 }}>
                <Info fontSize="small" />
              </ListItemIcon>
              <ListItemText primary={item} />
            </ListItem>
          ))}
        </List>
      </Alert>
    </Stack>
  );

  const renderUploadingContent = () => (
    <Box sx={{ textAlign: 'center', py: 4 }}>
      <CircularProgress size={48} sx={{ mb: 2 }} />
      <Typography variant="h6" gutterBottom>
        {t('product.newImportDialog.processingTitle')}
      </Typography>
      <Typography variant="body2" color="text.secondary">
        {t('product.newImportDialog.processingDescription')}
      </Typography>
    </Box>
  );

  const getDialogTitle = () => {
    switch (uploadState) {
      case 'success':
        return t('product.newImportDialog.dialogTitles.success');
      case 'error':
        return t('product.newImportDialog.dialogTitles.error');
      case 'uploading':
        return t('product.newImportDialog.dialogTitles.uploading');
      default:
        return t('product.newImportDialog.dialogTitles.idle');
    }
  };

  return (
    <Dialog
      {...props}
      onClose={handleClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: '28px',
          overflow: 'hidden',
          boxShadow: '0 22px 48px rgba(40, 19, 2, 0.18)',
        },
      }}
    >
      <DialogTitle
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          px: 3,
          py: 2.25,
          borderBottom: '1px solid rgba(109, 50, 7, 0.12)',
          background:
            'linear-gradient(180deg, rgba(255,247,232,0.85) 0%, rgba(255,255,255,1) 100%)',
        }}
      >
        {getDialogTitle()}
        <IconButton onClick={handleClose} size="small">
          <Close />
        </IconButton>
      </DialogTitle>

      <DialogContent
        sx={{
          minWidth: '520px',
          minHeight: '240px',
          px: 3,
          py: 2.5,
          backgroundColor: '#FFFCF8',
        }}
      >
        {uploadState === 'idle' && renderIdleContent()}
        {uploadState === 'uploading' && renderUploadingContent()}
        {uploadState === 'success' && renderSuccessContent()}
        {uploadState === 'error' && (
          <Box>
            <Typography variant="body1" color="error.main" sx={{ mb: 2 }}>
              {t('product.newImportDialog.errorIntro')}
            </Typography>
            {renderErrorList()}

            <Divider sx={{ my: 2 }} />

            <Alert severity="info">
              <AlertTitle>{t('product.newImportDialog.helpTitle')}</AlertTitle>
              {t('product.newImportDialog.helpLine1')}
              <br />
              {t('product.newImportDialog.helpLine2')}
              <br />
              {t('product.newImportDialog.helpLine3')}
            </Alert>
          </Box>
        )}
      </DialogContent>

      <DialogActions
        sx={{
          px: 3,
          py: 2.25,
          borderTop: '1px solid rgba(109, 50, 7, 0.12)',
          backgroundColor: '#fff',
        }}
      >
        {uploadState === 'idle' && (
          <>
            <Button onClick={handleClose}>
              {t('product.newImportDialog.actions.cancel')}
            </Button>
            <Button
              disabled={!file || !defaultEcoinventVersion}
              onClick={handleUploadClick}
              variant="contained"
              startIcon={<Upload />}
            >
              {t('product.newImportDialog.actions.import')}
            </Button>
          </>
        )}

        {uploadState === 'uploading' && (
          <Button onClick={handleClose} disabled>
            {t('product.newImportDialog.actions.processing')}
          </Button>
        )}

        {uploadState === 'success' && (
          <>
            <Button onClick={handleClose}>
              {t('product.newImportDialog.actions.uploadLater')}
            </Button>
            <Button
              onClick={handleOpenProduct}
              variant="contained"
              disabled={!successData?.product_id}
              startIcon={<FolderOpen />}
            >
              {t('product.newImportDialog.actions.openProduct')}
            </Button>
          </>
        )}

        {uploadState === 'error' && (
          <>
            <Button onClick={handleClose}>
              {t('product.newImportDialog.actions.close')}
            </Button>
            <Button onClick={() => resetState(false)} variant="contained">
              {t('product.newImportDialog.actions.tryAgain')}
            </Button>
          </>
        )}
      </DialogActions>
    </Dialog>
  );
}
